import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TripScoreBadge from './trip-score-badge';
import { getDateLabel, formatTripTime, formatDuration, formatDistance, normalizeScore } from './trip-utils';
import { useDriverScoreVisibility } from '@acorn-portal/shared/src/hooks/useDriverScoreVisibility';

/**
 * Props for the TripSummaryHeader component.
 */
interface TripSummaryHeaderProps {
	/** Trip start time in UTC. */
	startTimeUtc?: Date;
	/** Trip duration in seconds. */
	durationSeconds?: number;
	/** Trip distance in miles. */
	distanceMiles?: number;
	/** Starting address text. */
	startAddress?: string;
	/** Ending address text. */
	endAddress?: string;
	/** Total trip score (raw, will be normalized for display). */
	totalScore?: number;
	/** Callback fired when back button is clicked. */
	onBack: () => void;
}

/**
 * Header component for the trip details page.
 *
 * Displays comprehensive trip information including a back navigation button,
 * title, date/time, duration, distance, score badge, and start/end addresses
 * with connecting dotted line icons. The score badge is positioned absolutely
 * in the top-right corner.
 *
 * @component
 * @example
 * ```tsx
 * <TripSummaryHeader
 *   startTimeUtc={new Date('2026-01-28T10:30:00Z')}
 *   durationSeconds={1800}
 *   distanceMiles={15.5}
 *   startAddress="123 Main St, London"
 *   endAddress="456 Oak Ave, London"
 *   totalScore={7.5}
 *   onBack={() => navigate(-1)}
 * />
 * ```
 */
const TripSummaryHeader: React.FC<TripSummaryHeaderProps> = ({
	startTimeUtc,
	durationSeconds,
	distanceMiles,
	startAddress,
	endAddress,
	totalScore,
	onBack,
}) => {
	const theme = useTheme();
	const showDriverScore = useDriverScoreVisibility();

	const dateLabel = startTimeUtc ? getDateLabel(new Date(startTimeUtc)) : '';
	const timeString = startTimeUtc ? formatTripTime(new Date(startTimeUtc)) : '';
	const durationString = formatDuration(durationSeconds ?? 0);
	const distanceString = formatDistance(distanceMiles ?? 0);

	return (
		<Box sx={{ position: 'relative' }}>
			{/* Back Button */}
			<Button
				startIcon={<ArrowBackIcon />}
				onClick={onBack}
				sx={{
					color: theme.palette.secondary.main,
					textTransform: 'none',
					pl: 0,
					mb: 1,
					'&:hover': {
						backgroundColor: 'transparent',
					},
				}}
			>
				BACK
			</Button>

			{/* Title */}
			<Typography
				variant='h5'
				component='h1'
				color='secondary.main'
				sx={{
					fontWeight: 'bold',
					mb: 1.5,
				}}
			>
				Trip summary
			</Typography>

			{/* Score Badge - positioned absolutely on the right (hidden when driver score visibility flag is off) */}
			{showDriverScore && totalScore !== undefined && (
				<Box
					sx={{
						position: 'absolute',
						top: theme.spacing(5),
						right: 0,
					}}
				>
					<TripScoreBadge score={normalizeScore(totalScore)} />
				</Box>
			)}

			{/* Date and Time */}
			<Typography
				variant='body1'
				sx={{
					color: theme.palette.text.primary,
					fontWeight: 'bold',
					mb: 0.5,
				}}
			>
				{dateLabel}, {timeString}
			</Typography>

			{/* Duration and Distance */}
			<Typography
				variant='body2'
				sx={{
					color: theme.palette.text.secondary,
					mb: 2,
				}}
			>
				{durationString} • {distanceString}
			</Typography>

			{/* Addresses with icons and dotted line */}
			<Box sx={{ display: 'flex', pr: 6 }}>
				{/* Icons column with dotted line */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						mr: 1,
					}}
				>
					<GpsFixedIcon
						sx={{
							fontSize: 18,
							color: theme.palette.text.primary,
						}}
					/>
					{/* Dotted line connecting icons */}
					<Box
						sx={{
							width: 0,
							height: 16,
							borderLeft: `2px dashed ${theme.palette.grey[400]}`,
							my: 0.5,
						}}
					/>
					<LocationOnOutlinedIcon
						sx={{
							fontSize: 18,
							color: theme.palette.text.primary,
						}}
					/>
				</Box>

				{/* Address text column */}
				<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 0.25 }}>
					{startAddress && (
						<Typography variant='body2' color='text.primary'>
							{startAddress}
						</Typography>
					)}
					{endAddress && (
						<Typography variant='body2' color='text.primary' sx={{ mt: 'auto' }}>
							{endAddress}
						</Typography>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default TripSummaryHeader;
